import { type GamesItem, PAGE_SIZE_MAX, type Paginated, type SportOption } from "@seagames/contracts";
import { queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { OPTIONS_STALE_MS, retryTransient } from "../api/queries";

/**
 * The shell's own reads: which games exist (with their dates, for the default
 * choice) and the sports of one games (the sidebar's Meets ▾ list). Both are
 * ONE bounded request, change at setup time only, and are cached long; nothing
 * refetches on window focus.
 */
export const gamesKeys = {
	list: ["games", "list"] as const,
	sports: (gamesId: string) => ["sports", "options", gamesId] as const,
};

/** Options order (`GET /games/options`): main games first, then by code. */
export function sortGames<T extends { is_para: boolean; code: string }>(games: readonly T[]): T[] {
	return [...games].sort((a, b) => Number(a.is_para) - Number(b.is_para) || a.code.localeCompare(b.code));
}

/**
 * `GET /games` — every games with its time zone and dates. One page: a
 * platform runs a handful of games (SEA Games + ASEAN Para Games), the page
 * is capped at the API's maximum anyway.
 */
export function gamesListQuery() {
	return queryOptions({
		queryKey: gamesKeys.list,
		queryFn: async ({ signal }) => {
			const page = await api<Paginated<GamesItem>>("GET", "/games", { query: { page: 1, page_size: PAGE_SIZE_MAX }, signal });
			return sortGames(page.items);
		},
		staleTime: OPTIONS_STALE_MS,
		refetchOnWindowFocus: false,
		retry: retryTransient,
	});
}

/** `GET /sports/options?games_id=` — the active sports of one games, in programme order. */
export function gamesSportsQuery(gamesId: string) {
	return queryOptions({
		queryKey: gamesKeys.sports(gamesId),
		queryFn: ({ signal }) => api<SportOption[]>("GET", "/sports/options", { query: { games_id: gamesId }, signal }),
		staleTime: OPTIONS_STALE_MS,
		refetchOnWindowFocus: false,
		retry: retryTransient,
	});
}
